import type {
  LoadBalancingScheduleEmploymentType,
  LoadBalancingScheduleShiftPreference as LoadBalancingScheduleShiftPreferenceType,
  LoadBalancingShiftPatternPreference as LoadBalancingShiftPatternPreferenceType,
} from '@common/graphql';
import {
  sortSkillsByName,
  uniqueSkills,
  type WorkerSkillType,
} from './schedule-worker-skills-service';

export type TeamReviewTermInput = {
  id: string;
  active: boolean;
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
  employmentType: LoadBalancingScheduleEmploymentType;
  role?: string | null;
  homeUnit?: string | null;
  homeUnitId?: string | null;
  ftePercentage?: number | null;
  preferredTime: LoadBalancingScheduleShiftPreferenceType;
  shiftPatternPreference: LoadBalancingShiftPatternPreferenceType;
  chargeEligible: boolean;
  skills?: readonly WorkerSkillType[] | null;
};

export type TeamReviewWorkerInput = {
  id: string;
  name: string;
  terms: TeamReviewTermInput[];
  periodSkills?: readonly { skill: WorkerSkillType }[] | null;
};

export type TeamReviewChange = {
  id: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  role: string | null;
  ftePercentage: number | null;
  preferredTime: LoadBalancingScheduleShiftPreferenceType;
  shiftPatternPreference: LoadBalancingShiftPatternPreferenceType;
  chargeEligible: boolean;
  active: boolean;
  skills: WorkerSkillType[];
};

export type TeamReviewWorker = {
  id: string;
  name: string;
  role: string | null;
  unit: string | null;
  // NOTE: unitId is what the Unit filter matches on; unit is the home-unit name, which is
  // display-only and not guaranteed unique.
  unitId: string | null;
  employmentType: LoadBalancingScheduleEmploymentType;
  shiftType: LoadBalancingScheduleShiftPreferenceType;
  shiftPattern: LoadBalancingShiftPatternPreferenceType;
  ftePercentage: number | null;
  chargeEligible: boolean;
  status: 'active' | 'inactive';
  upcomingChanges: TeamReviewChange[];
  skills: WorkerSkillType[];
};

const SHIFT_PREFERENCE_LABEL: Record<
  LoadBalancingScheduleShiftPreferenceType,
  string
> = {
  DAY: 'Day',
  NIGHT: 'Night',
  EITHER: 'Flexible',
};

const EMPLOYMENT_LABEL: Record<LoadBalancingScheduleEmploymentType, string> = {
  CORE: 'Core',
  AGENCY: 'Agency',
};

const SHIFT_PATTERN_LABEL: Record<
  LoadBalancingShiftPatternPreferenceType,
  string
> = {
  CONSECUTIVE: 'Consecutive',
  NON_CONSECUTIVE: 'Non-consecutive',
  NO_PREFERENCE: 'No Shift Pattern',
};

export function shiftPreferenceLabel(
  preference: LoadBalancingScheduleShiftPreferenceType
): string {
  return SHIFT_PREFERENCE_LABEL[preference];
}

export function employmentLabel(
  employmentType: LoadBalancingScheduleEmploymentType
): string {
  return EMPLOYMENT_LABEL[employmentType];
}

export function shiftPatternLabel(
  shiftPattern: LoadBalancingShiftPatternPreferenceType
): string {
  return SHIFT_PATTERN_LABEL[shiftPattern];
}

export function schedulingStatusLabel(
  active: boolean,
  includeExcludeEnabled: boolean
): string {
  if (includeExcludeEnabled) return active ? 'Included' : 'Excluded';
  return active ? 'Active' : 'Inactive';
}

export function ftePercentageLabel(ftePercentage: number | null): string {
  return ftePercentage === null
    ? '—'
    : `${Math.round(ftePercentage * 10) / 10}%`;
}

export function skillsLabel(skills: readonly WorkerSkillType[]): string {
  return skills.length === 0
    ? '—'
    : skills.map((skill) => skill.name).join(', ');
}

export type TermCoverageInput = {
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
  id?: string;
};

function sortByStart<T extends TermCoverageInput>(terms: readonly T[]): T[] {
  return [...terms].sort(
    (a, b) =>
      a.effectiveStartDate.localeCompare(b.effectiveStartDate) ||
      (a.id ?? '').localeCompare(b.id ?? '')
  );
}

// NOTE: strict coverage with no neighbouring-term fallback, mirroring the server's
// WorkerTerm.effective_on containment check, so an uncovered date fails closed.
export function findCoveringTerm<T extends TermCoverageInput>(
  terms: readonly T[],
  asOf: string
): T | undefined {
  // NOTE: term bounds are date-only ISO strings, so a lexical compare is chronological. An
  // ISO instant would break at the 'T', which sorts after '-', hence the slice.
  const on = asOf.slice(0, 10);
  return sortByStart(terms).find((term) => {
    const end = term.effectiveEndDate ?? null;
    return term.effectiveStartDate <= on && (end === null || end >= on);
  });
}

export function pickCurrentTerm<T extends TermCoverageInput>(
  terms: readonly T[],
  asOf: string
): T | undefined {
  if (terms.length === 0) return undefined;

  const covering = findCoveringTerm(terms, asOf);
  if (covering) return covering;

  const sorted = sortByStart(terms);
  const started = sorted.filter((term) => term.effectiveStartDate <= asOf);
  return started.length > 0 ? started[started.length - 1] : sorted[0];
}

function toChange(term: TeamReviewTermInput): TeamReviewChange {
  return {
    id: term.id,
    effectiveStartDate: term.effectiveStartDate,
    effectiveEndDate: term.effectiveEndDate ?? null,
    role: term.role ?? null,
    ftePercentage: term.ftePercentage ?? null,
    preferredTime: term.preferredTime,
    shiftPatternPreference: term.shiftPatternPreference,
    chargeEligible: term.chargeEligible,
    active: term.active,
    skills: sortSkillsByName(term.skills ?? []),
  };
}

export function toTeamReviewWorker(
  worker: TeamReviewWorkerInput,
  asOf: string
): TeamReviewWorker {
  const current = pickCurrentTerm(worker.terms, asOf);
  const upcomingChanges = sortByStart(worker.terms)
    .filter((term) => term.effectiveStartDate > asOf && term.id !== current?.id)
    .map(toChange);

  return {
    id: worker.id,
    name: worker.name,
    role: current?.role ?? null,
    unit: current?.homeUnit ?? null,
    unitId: current?.homeUnitId ?? null,
    employmentType: current?.employmentType ?? 'CORE',
    shiftType: current?.preferredTime ?? 'EITHER',
    shiftPattern: current?.shiftPatternPreference ?? 'NO_PREFERENCE',
    ftePercentage: current?.ftePercentage ?? null,
    chargeEligible: current?.chargeEligible ?? false,
    status: current?.active ? 'active' : 'inactive',
    upcomingChanges,
    skills: uniqueSkills(
      (worker.periodSkills ?? []).map((periodSkill) => periodSkill.skill)
    ),
  };
}
